// @ts-nocheck
import { CartAddEvent, CartErrorEvent } from '@theme/events';

// Configuration
const CONFIG = {
  enableAutoBundling: false, // Set to true to enable automatic bundle creation from 12+ items
};
const CUSTOM_DOZEN_VARIANT_ID = window.location.href.includes("georgetown-cupcake-development.myshopify.com") ? "41451295047813" : '47500743114979';

class ProductCardControl {
  constructor() {
    this.cards = new Set();
    this.deliveryDate = localStorage.getItem('delivery-date');
    this.deliveryZip = localStorage.getItem('delivery-zip');
    this.deliveryType = localStorage.getItem('delivery-type');
    this.deliveryLocation = localStorage.getItem('delivery-location');
    this.deliveryTime = localStorage.getItem('delivery-time');
    this.today = new Date().toLocaleDateString('en-GB', { 
          day: '2-digit',
          month: 'long', 
          year: 'numeric' 
      });
    
    // Get zip codes from script tag data attributes
    const scriptTag = document.querySelector('script[src*="product-card-control.js"]');
    this.bethesdaZips = (scriptTag?.getAttribute('data-bethesda') || '').split(',').filter(z => z);
    this.georgetownZips = (scriptTag?.getAttribute('data-georgetown') || '').split(',').filter(z => z);
    
    this.init();
    this.pendingRetries = new WeakSet();
  }

  init() {
    // Watch for existing cards on page load
    this.observeExistingCards();
    
    // Setup MutationObserver to watch for new cards added to DOM
    this.setupCardObserver();
    
    // Listen for delivery selector changes
    this.setupDeliveryListener();
    
    // Listen for localStorage changes (from other tabs/windows)
    this.setupStorageListener();
    
    // Bind add to cart button clicks
    this.bindAddToCartEvents();
    
    // Initial update of all cards
    this.updateAllCards();
  }

  /**
   * Find and track all existing product cards on the page
   */
  observeExistingCards() {
    const cards = document.querySelectorAll('.product-card-wrapper');
    cards.forEach(card => {
      this.cards.add(card);
    });
  }

  /**
   * Setup MutationObserver to watch for new cards being added to DOM
   */
  setupCardObserver() {
    this.cardObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          // Check if added node is a card
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.classList?.contains('product-card-wrapper')) {
              this.cards.add(node);
              this.updateCard(node);
            }
            
            // Check if added node contains cards
            const nestedCards = node.querySelectorAll?.('.product-card-wrapper');
            if (nestedCards?.length) {
              nestedCards.forEach(card => {
                this.cards.add(card);
                this.updateCard(card);
              });
            }
          }
        });
        
        mutation.removedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.classList?.contains('product-card-wrapper')) {
              this.cards.delete(node);
            }
            
            const nestedCards = node.querySelectorAll?.('.product-card-wrapper');
            if (nestedCards?.length) {
              nestedCards.forEach(card => {
                this.cards.delete(card);
              });
            }
          }
        });
      });
    });

    // Start observing the document body for changes
    this.cardObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Listen for custom delivery-changed event from delivery-selector.js
   */
  setupDeliveryListener() {
    document.addEventListener('delivery-changed', () => {
      this.handleDeliveryChange();
    });
  }

  /**
   * Listen for localStorage changes (cross-tab synchronization)
   */
  setupStorageListener() {
    window.addEventListener('storage', (e) => {
      if (e.key === 'delivery-date' || 
          e.key === 'delivery-zip' || 
          e.key === 'delivery-type' || 
          e.key === 'delivery-location' || 
          e.key === 'delivery-time') {
        this.handleDeliveryChange();
      }
    });
  }

  /**
   * Handle delivery settings change
   */
  handleDeliveryChange() {
    const newDeliveryDate = localStorage.getItem('delivery-date');
    const newDeliveryZip = localStorage.getItem('delivery-zip');
    const newDeliveryType = localStorage.getItem('delivery-type');
    const newDeliveryLocation = localStorage.getItem('delivery-location');
    const newDeliveryTime = localStorage.getItem('delivery-time');
    
    // Check if anything actually changed (including null values)
    const changed = 
      this.deliveryDate !== newDeliveryDate ||
      this.deliveryZip !== newDeliveryZip ||
      this.deliveryType !== newDeliveryType ||
      this.deliveryLocation !== newDeliveryLocation ||
      this.deliveryTime !== newDeliveryTime;
    
    if (changed) {
      // Store previous values for comparison
      const previousValues = {
        date: this.deliveryDate,
        zip: this.deliveryZip,
        type: this.deliveryType,
        location: this.deliveryLocation,
        time: this.deliveryTime
      };
      
      // Update to new values (including null)
      this.deliveryDate = newDeliveryDate;
      this.deliveryZip = newDeliveryZip;
      this.deliveryType = newDeliveryType;
      this.deliveryLocation = newDeliveryLocation;
      this.deliveryTime = newDeliveryTime;
    
      // Update all cards with new delivery settings
      this.updateAllCards();
    }
  }

  /**
   * Update all tracked product cards
   */
  updateAllCards() {
    // Re-scan DOM for any new cards that might have been added by filters
    this.observeExistingCards();
    
    // Update all tracked cards
    this.cards.forEach(card => {
      // Skip if card was removed from DOM
      if (card.isConnected) {
        this.updateCard(card);
      } else {
        this.cards.delete(card);
      }
    });
  }

  /**
   * Update a single product card based on current delivery settings
   * @param {HTMLElement} card - The product card element to update
   */
  updateCard(card) {
    // Validate zip code (5 digits)
    
    // Enable/disable add to cart button based on delivery data
    const currentType = localStorage.getItem('delivery-ship-type');
    let localTime = false;

    if (currentType === 'nationwide' || (currentType === "local" && this.deliveryTime)) {
      localTime = true;
    }
  }

  validateZip(zip) {
    // Check if zip is 5 digits
    if (!/^\d{5}$/.test(zip)) {
      return { isValid: false, isLocalEligible: false };
    }
    
    // Check if zip is eligible for local delivery
    const isLocalEligible = this.bethesdaZips.includes(zip) || this.georgetownZips.includes(zip);
    
    return { isValid: true, isLocalEligible };
  }

  async waitForCartEmpty(timeout = 20000, interval = 500) {
    const start = Date.now();
    while (Date.now() - start < timeout) {
      try {
        const cart = await fetch('/cart.js').then(res => res.json());
        if (cart && Array.isArray(cart.items) && cart.items.length === 0) return true;
      } catch (err) {
        // ignore and retry
      }
      await new Promise(r => setTimeout(r, interval));
    }
    return false;
  }

  /**
   * Bind add to cart button click events
   */
  bindAddToCartEvents() {
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.js-add-to-cart');
      const card = e.target.closest('.product-card-wrapper');
      
      if (!button || !card) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      this.handleAddToCart(card, button);
    });
  }

  /**
   * Handle add to cart action using localStorage data
   */
  async handleAddToCart(card, button) {
    const productId = button.getAttribute('data-product-id');
    
    if (!productId) {
      console.error('Product ID not found');
      return;
    }

    // Find quantity selector component near the button (in the same parent context)
    const buttonParent = button.closest('.product-card__details-actions, .product-card__actions');
    const quantitySelector = buttonParent?.querySelector('quantity-selector-component');
    const quantityInput = quantitySelector?.querySelector('input[name="quantity"]');
    const locationInput = document.querySelector('.js-location');
    const location = locationInput ? locationInput.value : 'nationwide';
    const availableQty = quantityInput.getAttribute(`data-inventory-${location}`);
    let isFlavor = false;
    
    if (!quantityInput) {
      console.error('Quantity input not found');
      return;
    }
    
    const quantity = parseInt(quantityInput.value, 10) || 0;
    
    // Don't add to cart if quantity is 0
    if (quantity === 0 || isNaN(quantity)) {
      return;
    }

    const currentCart = await fetch('/cart.js').then(res => res.json());
    const currentType = currentCart.attributes?.type || null;
    const activeType = currentCart.attributes?.activeType || null;

    if (activeType && currentType && activeType !== currentType && currentCart.items.length > 0) {
      document.dispatchEvent(new CustomEvent('open-purchase-blocking-popup'));

      if (this.pendingRetries.has(button)) return;
      this.pendingRetries.add(button);

      const clearClickHandler = async (e) => {
        if (!e.target) return;
        const el = e.target.closest && e.target.closest('.js-cart-clear');
        if (!el) return;

        document.removeEventListener('click', clearClickHandler);

        const emptied = await this.waitForCartEmpty(20000, 500);

        this.pendingRetries.delete(button);

        if (emptied) {
          setTimeout(() => {
            this.handleAddToCart(card, button);
          }, 300);
        } else {
          try { button.disabled = false; } catch (err) {}
        }
      };

      document.addEventListener('click', clearClickHandler);
      return;
    }

    // Parse selected month from delivery date (format: "DD Month YYYY")
    let selectedMonth = '';
    if (this.deliveryDate) {
      const dateParts = this.deliveryDate.split(' ');
      selectedMonth = dateParts[1] || '';
    }
    
    // Check if pickup block is hidden - if so, clear local location and type
    const pickupBlock = document.querySelector('.delivery-selector__block--pickup');
    const isPickupHidden = pickupBlock?.classList.contains('delivery-selector__block--hidden');
    
    // Validate zip for local delivery eligibility
    const body = {
      id: productId,
      quantity: quantity,
      properties: {
        _availableMonth: selectedMonth,
        deliveryDate: this.deliveryDate || '',
        deliveryZip: this.deliveryZip || '',
        deliveryTime: this.deliveryTime || '',
        deliveryLocalLocation: this.deliveryLocation || '',
        deliveryLocalType: this.deliveryType || '',
      },
      attributes: {
        activeType: currentType || ""
      }
    };
    
    if(button.closest('[data-dozen]')) {
      body.properties._uniqueId = Date.now().toString();
    }

    if(button.closest('[data-product-flavor]')) {
      body.properties._itemType = 'flavor';
      isFlavor = true;
    }

    // Disable button during request
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Adding...';
    
    fetch('/cart.js', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
    })
    .then((response) => response.json())
    .then((cart) => {
      let isAvailable = true;
      const isMerch = quantityInput?.dataset?.merch === 'true';
      
      if (isFlavor) {
        if(cart.items.length > 0 && currentType === 'local' && this.today === this.deliveryDate) {
          cart.items.forEach((item) => {
            if(item.id.toString() === productId.toString() && item.properties['deliveryDate'] === this.today && item.properties['deliveryLocalLocation'] === this.deliveryLocation) {
              if(availableQty < (quantity + item.quantity)) {
                isAvailable = false;
                return;
              }
            }
          })
        } 
      } else if (isMerch) {
        let qtyInCart = 0;
        cart.items.forEach((item) => {
          if (location !== 'nationwide') {
            if (+item.variant_id === +productId && item.properties?.['deliveryLocalLocation'] === location) {
              qtyInCart += item.quantity;
            }
          } else {
            if (+item.variant_id === +productId && !item.properties?.['deliveryLocalLocation']) {
              qtyInCart += item.quantity;
            }
          }
        });
        
        if (availableQty && qtyInCart + quantity > parseInt(availableQty, 10)) {
          isAvailable = false;
        }
      }
      
      if(!isAvailable){
        const popup = document.querySelector('.js-popup-inventory');
        popup.classList.remove('hidden');
        popup.querySelector('.js-popup-text').textContent = availableQty;
        button.textContent = originalText;
        button.disabled = false;
        return;
      }
      fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          document.dispatchEvent(
            new CartErrorEvent('', response.message, response.description, response.errors)
          );
        } else {
          document.dispatchEvent(new CartAddEvent());
          
          if (quantityInput) {
            quantityInput.classList.remove('is-active');
            quantityInput.value = '0';
            quantityInput.setAttribute('value', '0');
            quantityInput.dispatchEvent(new Event('blur', { bubbles: true }));
          }
          
          // Check and auto-generate bundles if 12+ items exist (only if enabled)
          if (CONFIG.enableAutoBundling) {
            this.checkAndGenerateBundle(currentType);
          }
          
          // Update loyalty widget if function exists
          if (window.updateLoyaltyPointsText) {
            window.updateLoyaltyPointsText();
          }
        }
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
        document.dispatchEvent(
          new CartErrorEvent('', 'Failed to add to cart', error.message, {})
        );
      })
      .finally(() => {
        // Re-enable button based on current delivery data
        button.disabled = false;
        button.textContent = originalText;
        this.updateCard(card);
      });
    })
  }

  /**
   * Checks cart and auto-generates bundles from individual flavor items when reaching 12+
   */
  checkAndGenerateBundle(type) {
    const currentType = document.querySelector('[data-cart-type]')?.getAttribute('data-cart-type');
    
    if (currentType && currentType !== type) {
      return;
    }
    
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        if (cart.items.length > 0) {
          const cartObject = {};
          cart.items.reverse().forEach((item) => {
            // Only bundle individual curated items, not custom-dozen
            if (item.handle !== 'custom-dozen' && !item.properties._itemType) {
              const deliveryDate = item.properties.deliveryDate || '';
              const deliveryZip = item.properties.deliveryZip || '';
              const deliveryLocalLocation = item.properties.deliveryLocalLocation || '';
              const deliveryLocalType = item.properties.deliveryLocalType || '';
              
              const groupKey = `${deliveryDate}|${deliveryZip}|${deliveryLocalLocation}|${deliveryLocalType}`;
              
              if (!cartObject[groupKey]) {
                cartObject[groupKey] = [];
              }
              cartObject[groupKey].push(item);
            }
          });
          
          // Generate bundles for groups with 12+ items
          Object.keys(cartObject).forEach(groupKey => {
            let groupQty = 0;
            cartObject[groupKey].forEach(item => {
              groupQty += item.quantity;
            });

            if (groupQty >= 12) {
              this.generateBundle(cartObject[groupKey], type);
            }
          });
        }
      })
      .catch(error => console.error('Error checking cart for bundles:', error));
  }

  /**
   * Generates and adds bundle to cart from individual curated items
   */
  generateBundle(items, type) {
    let totalQty = 0;
    let flavors = [];
    let ids = [];
    let bundledFlavors = [];
    let partBundledItem;
    let partBundledQty;
    
    items.forEach(item => {
      if (totalQty < 12) {
        if (totalQty + item.quantity <= 12) {
          totalQty += item.quantity;
          flavors.push(`${item.title}:${item.quantity}`);
          ids.push(item.variant_id);
          bundledFlavors.push(item);
        } else {
          partBundledItem = item;
          partBundledQty = 12 - totalQty;
          flavors.push(`${item.title}:${partBundledQty}`);
          ids.push(item.variant_id);
        }
      }
    });

    const updates = {};
    let additionalBundlesQty = 0;
    let remainder = 0;
    
    // Remove bundled items from cart
    bundledFlavors.forEach(item => {
      updates[item.key] = 0;
    });
    
    if (partBundledItem && partBundledItem.quantity - partBundledQty > 12) {
      additionalBundlesQty = Math.floor((partBundledItem.quantity - partBundledQty) / 12);
      remainder = (partBundledItem.quantity - partBundledQty) % 12;
    }
    
    if (partBundledItem && additionalBundlesQty === 0) {
      updates[partBundledItem.key] = partBundledItem.quantity - partBundledQty;
    } else if (partBundledItem && additionalBundlesQty > 0) {
      updates[partBundledItem.key] = remainder;
    }

    // Update cart to remove bundled items
    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ updates })
    })
    .then(res => res.json())
    .then(cart => {
      const firstItem = items[0];
      const deliveryProperties = {
        _availableMonth: firstItem.properties._availableMonth || '',
        _uniqueId: Date.now().toString(),
        deliveryDate: firstItem.properties.deliveryDate || '',
        deliveryZip: firstItem.properties.deliveryZip || '',
        deliveryTime: firstItem.properties.deliveryTime || '',
        deliveryLocalLocation: firstItem.properties.deliveryLocalLocation || '',
        deliveryLocalType: firstItem.properties.deliveryLocalType || '',
      };
      
      const body = {
        items: [{
          id: CUSTOM_DOZEN_VARIANT_ID, // Custom dozen bundle product ID
          quantity: 1,
          properties: {
            _flavors: flavors.join('//'),
            _flavorIds: ids.join(','),
            _type: type,
            ...deliveryProperties
          },
          attributes: {
            activeType: type
          }
        }]
      };
      
      // Add additional full bundles if needed
      if (additionalBundlesQty > 0) {
        body.items.push({
          id: CUSTOM_DOZEN_VARIANT_ID,
          quantity: additionalBundlesQty,
          properties: {
            _flavors: `${partBundledItem.title}:12`,
            _type: type,
            ...deliveryProperties
          },
          attributes: {
            activeType: type
          }
        });
      }
      
      // Add bundle to cart
      return fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
    })
    .then((response) => response.json())
    .then((response) => {
      if (response.status) {
        const event = new CartErrorEvent('', response.message, response.description, response.errors);
        document.dispatchEvent(event);
      } else {
        document.dispatchEvent(new CartAddEvent());
        if (window.updateLoyaltyPointsText) {
          window.updateLoyaltyPointsText();
        }
      }
    })
    .catch(error => console.error('Error generating bundle:', error));
  }

  /**
   * Cleanup observers
   */
  destroy() {
    if (this.cardObserver) {
      this.cardObserver.disconnect();
    }
    this.cards.clear();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productCardControl = new ProductCardControl();
  });
} else {
  window.productCardControl = new ProductCardControl();
}
